import type { NavItem } from "@dl/web-core-js/shell";
import {
  Archive,
  ArchiveX,
  ArrowLeftRight,
  ArrowRightLeft,
  BadgePercent,
  Ban,
  Banknote,
  Boxes,
  Braces,
  Building2,
  CalendarRange,
  ChartLine,
  ClipboardCheck,
  ClipboardList,
  Cog,
  Coins,
  Container,
  DollarSign,
  DraftingCompass,
  Drill,
  Eraser,
  Factory,
  FileText,
  FolderTree,
  Gauge,
  Grid3x3,
  Hammer,
  HandCoins,
  HardHat,
  KeyRound,
  Layers,
  LayoutDashboard,
  Library,
  ListChecks,
  Monitor,
  MonitorPlay,
  Package,
  PackageCheck,
  PackageOpen,
  PackagePlus,
  Palette,
  PenTool,
  Percent,
  Play,
  Receipt,
  Recycle,
  Ruler,
  Scale,
  ScanBarcode,
  ScanLine,
  Scissors,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  SlidersHorizontal,
  SlidersVertical,
  SquarePen,
  Store,
  Tag,
  Tags,
  TrendingUp,
  Truck,
  Users,
  Warehouse,
  Webhook,
  Workflow,
  Wrench,
} from "lucide-react";
import { NexpimView } from "./views";

/**
 * The nexpim menu.
 *
 * In the order work flows: what is sold, what a product is made of, what is
 * bought, what Laval makes (Fabrication), what Terrebonne boxes (Assembly),
 * what is held (Stock). Fabrication and Assembly each have their shop floor
 * and their station kiosk. SIGM's views sit apart until SIGM is gone. Settings
 * holds the one page of settings every section reads, then the reference
 * data, grouped by who owns it.
 *
 * Every icon is used once. Before this the icons were shared by accident, six
 * entries on Percent alone, which defeats the point: in the collapsed rail the
 * icon is all you see.
 */
export const nav: NavItem[] = [
  { labelKey: "navigation.item.dashboard", to: "/", icon: LayoutDashboard, view: NexpimView.HOME },
  { labelKey: "navigation.item.products", to: "/products", icon: Package, view: NexpimView.PRODUCTS },

  {
    labelKey: "navigation.item.sales",
    icon: TrendingUp,
    defaultOpen: true,
    children: [
      { labelKey: "navigation.item.sales-analysis", to: "/sales/analysis", icon: ChartLine, view: NexpimView.SALES_ANALYSIS },
      { labelKey: "navigation.item.sales-client-margin", to: "/sales/client-margin", icon: Percent, view: NexpimView.SALES_CLIENT_MARGIN },
      { labelKey: "navigation.item.sales-orders", to: "/sales/orders", icon: ShoppingCart, view: NexpimView.SALES_ORDERS },
      { labelKey: "navigation.item.sales-invoices", to: "/sales/invoices", icon: Receipt, view: NexpimView.SALES_INVOICES },
      { labelKey: "navigation.item.manual-invoices", to: "/manual-invoices", icon: FileText, view: NexpimView.MANUAL_INVOICES },
    ],
  },

  // What a product is made of: its parts, their bills and routings.
  {
    labelKey: "navigation.item.engineering",
    icon: DraftingCompass,
    children: [
      { labelKey: "navigation.item.manufacturing-parts", to: "/manufacturing/parts", icon: Boxes, view: NexpimView.MANUFACTURING_PARTS },
      {
        // Each part's steps, to check and approve: lots are released only on approved routings.
        labelKey: "navigation.item.routings",
        to: "/manufacturing/routings",
        icon: Workflow,
        view: NexpimView.MANUFACTURING_PARTS,
      },
      // The configurator's data (0071): colours, sister parts, the model grid, the cleanup rules.
      { labelKey: "navigation.item.engineering-colours", to: "/engineering/colours", icon: Palette, view: NexpimView.MANUFACTURING_PARTS },
      { labelKey: "navigation.item.engineering-models", to: "/engineering/models", icon: Grid3x3, view: NexpimView.MANUFACTURING_PARTS },
      { labelKey: "navigation.item.engineering-cleanup", to: "/engineering/cleanup", icon: Eraser, view: NexpimView.MANUFACTURING_PARTS },
    ],
  },

  // Purchasing: what to buy (replenishment), what was bought (orders), from
  // whom, what it costs (cash) and whether the rules hold (reviews).
  {
    labelKey: "navigation.item.purchasing",
    icon: PackageCheck,
    children: [
      { labelKey: "navigation.item.replenishment", to: "/purchasing/replenishment", icon: Gauge, view: NexpimView.PURCHASING },
      { labelKey: "navigation.item.purchase-orders", to: "/purchasing/purchase-orders", icon: ClipboardList, view: NexpimView.PURCHASING },
      { labelKey: "navigation.item.suppliers", to: "/manufacturing/suppliers", icon: Truck, view: NexpimView.SUPPLIERS },
      { labelKey: "navigation.item.order-units", to: "/purchasing/order-units", icon: Container, view: NexpimView.PURCHASING },
      { labelKey: "navigation.item.purchasing-reviews", to: "/purchasing/reviews", icon: ClipboardCheck, view: NexpimView.PURCHASING },
      { labelKey: "navigation.item.cash-projection", to: "/purchasing/cash", icon: Banknote, view: NexpimView.PURCHASING },
    ],
  },

  // Laval: the floor, what to make (the pipeline), its orders (their trucks are on each order).
  {
    labelKey: "navigation.item.fabrication",
    icon: Factory,
    defaultOpen: true,
    children: [
      { labelKey: "navigation.item.shop-floor", to: "/production/floor", icon: MonitorPlay, view: NexpimView.SHOP_FLOOR },
      { labelKey: "navigation.item.station", to: "/production/station", icon: ScanBarcode, view: NexpimView.SHOP_FLOOR_STATION },
      { labelKey: "navigation.item.fabrication-planning", to: "/production/pipeline", icon: CalendarRange, view: NexpimView.PRODUCTION_PIPELINE },
      { labelKey: "navigation.item.production-orders", to: "/production/orders", icon: Hammer, view: NexpimView.PRODUCTION_ORDERS },
      // The lots Cut Rite batched, cut as one at the saw (docs/cutting-jobs).
      { labelKey: "navigation.item.cut-jobs", to: "/production/cut-jobs", icon: Scissors, view: NexpimView.PRODUCTION_ORDERS },
      {
        // Consumption against the engineering BOMs, over completed orders.
        labelKey: "navigation.item.production-scrap",
        to: "/production/scrap",
        icon: Recycle,
        view: NexpimView.PRODUCTION_ORDERS,
      },
    ],
  },

  // Terrebonne: the board, the kiosk (kit, foam, cornières, pack), its orders, the shelves.
  {
    labelKey: "navigation.item.assembly",
    icon: PackageOpen,
    defaultOpen: true,
    children: [
      { labelKey: "navigation.item.assembly-floor", to: "/assembly/floor", icon: Monitor, view: NexpimView.TERREBONNE_PACKING },
      { labelKey: "navigation.item.assembly-station", to: "/assembly/station", icon: ScanLine, view: NexpimView.SHOP_FLOOR_STATION },
      { labelKey: "navigation.item.assembly-orders", to: "/assembly/orders", icon: PackagePlus, view: NexpimView.PRODUCTION_ORDERS },
      {
        // What is on each finished-goods shelf, and what is not on one yet.
        labelKey: "navigation.item.shelves",
        to: "/production/shelves",
        icon: Layers,
        view: NexpimView.TERREBONNE_PACKING,
      },
    ],
  },

  {
    labelKey: "navigation.item.stock-group",
    icon: Warehouse,
    children: [
      { labelKey: "navigation.item.stock", to: "/manufacturing/stock", icon: Warehouse, view: NexpimView.MANUFACTURING_STOCK },
      { labelKey: "navigation.item.stock-counts", to: "/manufacturing/stock/counts", icon: ListChecks, view: NexpimView.MANUFACTURING_STOCK },
      { labelKey: "navigation.item.stock-adjust", to: "/manufacturing/stock/adjust", icon: SquarePen, view: NexpimView.MANUFACTURING_STOCK },
      {
        // What to move between warehouses (NEXCA, NEXUS), as the pipeline sees the need.
        labelKey: "navigation.item.transfer-plan",
        to: "/production/transfers",
        icon: ArrowLeftRight,
        view: NexpimView.PRODUCTION_PIPELINE,
      },
      { labelKey: "navigation.item.stock-transfer", to: "/manufacturing/stock/transfer", icon: ArrowRightLeft, view: NexpimView.MANUFACTURING_STOCK },
      { labelKey: "navigation.item.stocking-points", to: "/manufacturing/stock/points", icon: Building2, view: NexpimView.MANUFACTURING_STOCK },
      { labelKey: "navigation.item.stock-inactive", to: "/manufacturing/stock/inactive", icon: ArchiveX, view: NexpimView.MANUFACTURING_STOCK },
    ],
  },

  {
    labelKey: "navigation.item.service-group",
    icon: Wrench,
    children: [
      { labelKey: "navigation.item.service-orders", to: "/servicedesk/orders", icon: Truck, view: NexpimView.SERVICE_DESK },
      { labelKey: "navigation.item.service-production", to: "/servicedesk/production", icon: Drill, view: NexpimView.SERVICE_DESK },
      { labelKey: "navigation.item.services", to: "/services", icon: ChartLine, view: NexpimView.SERVICES },
    ],
  },

  {
    labelKey: "navigation.item.pricing",
    icon: DollarSign,
    children: [
      { labelKey: "navigation.item.pricing-products", to: "/pricing/product", icon: Coins, view: NexpimView.PRICING_PRODUCT },
      { labelKey: "navigation.item.pricing-product-discount", to: "/pricing/product/discount", icon: BadgePercent, view: NexpimView.PRICING_PRODUCT_DISCOUNT },
      { labelKey: "navigation.item.pricing-rules", to: "/pricing/rules", icon: Scale, view: NexpimView.PRICING_RULES },
    ],
  },

  // SIGM's own views, read from its database: they go when SIGM does.
  {
    labelKey: "navigation.item.sigm",
    icon: Archive,
    children: [
      { labelKey: "navigation.item.production-projects", to: "/production/project", icon: ClipboardList, view: NexpimView.PRODUCTION_PROJECT },
      { labelKey: "navigation.item.parts", to: "/parts", icon: Boxes, view: NexpimView.PARTS },
    ],
  },

  {
    labelKey: "navigation.item.settings",
    icon: Settings,
    children: [
      { labelKey: "navigation.item.config-settings", to: "/config/settings", icon: SlidersVertical, view: NexpimView.CONFIG_SETTINGS },
      {
        labelKey: "navigation.item.setup-products",
        icon: Package,
        children: [
          { labelKey: "navigation.item.config-designer", to: "/config/product/designer", icon: PenTool, view: NexpimView.CONFIG_PRODUCT_DESIGNER },
          { labelKey: "navigation.item.config-collection", to: "/config/product/collection", icon: Library, view: NexpimView.CONFIG_PRODUCT_COLLECTION },
          { labelKey: "navigation.item.config-category", to: "/config/product/category", icon: FolderTree, view: NexpimView.CONFIG_PRODUCT_CATEGORY },
          { labelKey: "navigation.item.config-tag", to: "/config/product/tag", icon: Tag, view: NexpimView.CONFIG_PRODUCT_TAG },
          { labelKey: "navigation.item.config-metafield", to: "/config/product/metafield", icon: Braces, view: NexpimView.CONFIG_PRODUCT_METAFIELD },
          { labelKey: "navigation.item.config-cogs", to: "/config/product/cogs", icon: Banknote, view: NexpimView.CONFIG_PRODUCT_COGS },
          { labelKey: "navigation.item.config-cogs-expense", to: "/config/cogs/expense", icon: HandCoins, view: NexpimView.CONFIG_COGS_EXPENSE },
        ],
      },
      {
        // What parts are: groups, the virtual panels engineering BOMs consume, attributes, units.
        labelKey: "navigation.item.setup-parts",
        icon: Boxes,
        children: [
          { labelKey: "navigation.item.config-part-group", to: "/config/manufacturing/part-group", icon: Boxes, view: NexpimView.CONFIG_PART_GROUP },
          { labelKey: "navigation.item.config-virtual-part", to: "/config/manufacturing/virtual-part", icon: Layers, view: NexpimView.MANUFACTURING_PARTS },
          { labelKey: "navigation.item.config-attribute", to: "/config/manufacturing/attribute", icon: Braces, view: NexpimView.CONFIG_ATTRIBUTE },
          { labelKey: "navigation.item.config-unit", to: "/config/manufacturing/unit", icon: Ruler, view: NexpimView.CONFIG_UNIT },
          { labelKey: "navigation.item.config-measurement-kind", to: "/config/manufacturing/measurement-kind", icon: Ruler, view: NexpimView.CONFIG_UNIT },
        ],
      },
      {
        // Where and how the plants work.
        labelKey: "navigation.item.setup-floor",
        icon: HardHat,
        children: [
          { labelKey: "navigation.item.config-site", to: "/config/manufacturing/site", icon: Building2, view: NexpimView.CONFIG_PLANT },
          { labelKey: "navigation.item.config-department", to: "/config/manufacturing/department", icon: Users, view: NexpimView.CONFIG_PLANT },
          { labelKey: "navigation.item.config-operation", to: "/config/manufacturing/operation", icon: Wrench, view: NexpimView.CONFIG_PLANT },
          { labelKey: "navigation.item.config-process", to: "/config/manufacturing/process", icon: Workflow, view: NexpimView.CONFIG_PLANT },
          { labelKey: "navigation.item.config-station", to: "/config/manufacturing/station", icon: Cog, view: NexpimView.CONFIG_PLANT },
          { labelKey: "navigation.item.shop-floor-setup", to: "/config/shop-floor", icon: HardHat, view: NexpimView.CONFIG_SHOP_FLOOR },
          { labelKey: "navigation.item.config-stock-reason", to: "/config/manufacturing/stock-adjustment-reason", icon: SlidersHorizontal, view: NexpimView.MANUFACTURING_STOCK },
        ],
      },
      {
        labelKey: "navigation.item.setup-assembly",
        icon: PackageOpen,
        children: [
          {
            // When a product's assembly version is used: by market or client.
            labelKey: "navigation.item.config-version-rule",
            to: "/config/manufacturing/version-rule",
            icon: Layers,
            view: NexpimView.MANUFACTURING_PARTS,
          },
        ],
      },
      {
        labelKey: "navigation.item.setup-service",
        icon: Wrench,
        children: [
          { labelKey: "navigation.item.config-service-motive", to: "/config/servicedesk/motive", icon: SlidersHorizontal, view: NexpimView.SERVICE_DESK },
        ],
      },
    ],
  },

  {
    labelKey: "navigation.item.admin",
    icon: ShieldCheck,
    children: [
      {
        labelKey: "navigation.item.admin-actions",
        to: "/admin/actions",
        icon: Play,
        view: NexpimView.ADMIN_ACTIONS,
      },
      {
        labelKey: "navigation.item.users",
        to: "/users",
        icon: Users,
        view: NexpimView.ADMIN_ACCOUNTS,
      },
      {
        labelKey: "navigation.item.app-accounts",
        to: "/app-accounts",
        icon: KeyRound,
        view: NexpimView.ADMIN_ACCOUNTS,
      },
      {
        labelKey: "navigation.item.api-inventory-updates",
        to: "/api-inventory-updates",
        icon: Webhook,
        view: NexpimView.API_INVENTORY_UPDATES,
      },
      {
        labelKey: "navigation.item.sales-channels",
        to: "/sales-channels",
        icon: Store,
        view: NexpimView.API_INVENTORY_UPDATES,
      },
      {
        labelKey: "navigation.item.shopify-store",
        to: "/shopify/store",
        icon: ShoppingBag,
        view: NexpimView.API_INVENTORY_UPDATES,
      },
      {
        labelKey: "navigation.item.shopify-tag-sources",
        to: "/config/shopify/tag-source",
        icon: Tags,
        view: NexpimView.API_INVENTORY_UPDATES,
      },
      {
        labelKey: "navigation.item.config-automatic-discontinuation",
        to: "/config/automatic-product-discontinuation",
        icon: Ban,
        view: NexpimView.CONFIG_AUTOMATIC_PRODUCT_DISCONTINUATION,
      },
    ],
  },
];
